import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import style from "./signin.module.css";
import LogoSection from "./components/LogoSection";
import { getRegions } from "../../services/domain/regionsApiService";
import { authService } from "./services/authService";
import { storageService } from "./services/storageService";
import logger from "../../utils/logger";
import { generateMockPlots } from "../../utils/mockData";

function SelectRegion() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [isMenuAccess, setIsMenuAccess] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      const pendingAuthRaw = localStorage.getItem("pendingAuth");

      // Acesso pelo menu (usuário já autenticado)
      if (!pendingAuthRaw) {
        const token = storageService.getToken();
        if (!token) {
          navigate("/");
          return;
        }

        setIsMenuAccess(true);
        const enterprise = localStorage.getItem("enterprise") || "ACM";
        const isMock = storageService.isMockMode();

        if (isMock) {
          const mockRegions = [
            { value: "Norte", label: "Norte" },
            { value: "Sul", label: "Sul" },
          ];
          setRegions(mockRegions);
          setSelectedRegion(storageService.getRegion() || mockRegions[0].value);
          return;
        }

        try {
          const fetchedRegions = await getRegions(enterprise);
          const normalizedRegions = fetchedRegions
            .map((item) => {
              if (typeof item === "string") return { value: item, label: item };
              const value = item?.value || item?.region || item?.name || item?.descricao || "";
              const label = item?.label || value;
              if (!value) return null;
              return { value, label };
            })
            .filter(Boolean);
          setRegions(normalizedRegions);
          const currentRegion = storageService.getRegion();
          setSelectedRegion(currentRegion || (normalizedRegions.length > 0 ? normalizedRegions[0].value : ""));
          localStorage.setItem("regions", JSON.stringify(normalizedRegions));
        } catch (error) {
          logger.error("Erro ao buscar regiões", error);
          toast.error("Não foi possível carregar as regionais.");
        }
        return;
      }

      // Fluxo de login (pendingAuth presente)
      const pendingAuth = JSON.parse(pendingAuthRaw);
      const enterprise = pendingAuth?.enterprise || "ACM";

      if (pendingAuth?.isMock) {
        setRegions([
          { value: "Norte", label: "Norte" },
          { value: "Sul", label: "Sul" },
        ]);
        return;
      }

      try {
        const fetchedRegions = await getRegions(enterprise);
        const normalizedRegions = fetchedRegions
          .map((item) => {
            if (typeof item === "string") {
              return { value: item, label: item };
            }

            const value =
              item?.value ||
              item?.region ||
              item?.name ||
              item?.descricao ||
              "";
            const label = item?.label || value;

            if (!value) return null;
            return { value, label };
          })
          .filter(Boolean);
        setRegions(normalizedRegions);
        if (normalizedRegions.length > 0) {
          setSelectedRegion(normalizedRegions[0].value);
        }
        localStorage.setItem("regions", JSON.stringify(normalizedRegions));
      } catch (error) {
        logger.error("Erro ao buscar regiões", error);
        toast.error("Não foi possível carregar as regionais.");
      }
    };

    initialize();
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedRegion) {
      toast.error("Selecione uma regional.");
      return;
    }

    // Acesso pelo menu: apenas atualiza a regional e os plots
    if (isMenuAccess) {
      setLoading(true);
      try {
        const enterprise = localStorage.getItem("enterprise") || "ACM";
        storageService.setRegion(selectedRegion);
        await authService.getPlots(selectedRegion, enterprise);
        navigate("/home");
      } catch (error) {
        logger.error("Erro ao atualizar regional", error);
        toast.error("Não foi possível atualizar a regional.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Fluxo de login
    const pendingAuthRaw = localStorage.getItem("pendingAuth");
    if (!pendingAuthRaw) {
      navigate("/");
      return;
    }

    const pendingAuth = JSON.parse(pendingAuthRaw);

    setLoading(true);
    try {
      authService.saveAuthData(
        pendingAuth.token,
        pendingAuth.user,
        pendingAuth.password,
        selectedRegion,
        pendingAuth.enterprise || "ACM"
      );

      if (pendingAuth.isMock) {
        const mockPlots = generateMockPlots(selectedRegion);
        localStorage.setItem("allPlots", JSON.stringify(mockPlots));
      } else {
        await authService.getPlots(selectedRegion, pendingAuth.enterprise || "ACM");
      }

      localStorage.removeItem("pendingAuth");
      navigate("/home");
    } catch (error) {
      logger.error("Erro ao concluir login com regional", error);
      toast.error("Não foi possível concluir a seleção de regional.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={style["main-container"]}>
      <main className={style.container}>
        <LogoSection />
        <h2>Selecionar Regional</h2>

        <form onSubmit={handleSubmit}>
          <div className={style["input-field"]}>
            <p>
              Regional <span className={style["required"]}>*</span>
            </p>
            <select
              name="region"
              id="region"
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
            >
              {regions.map((region) => (
                <option key={region.value} value={region.value}>
                  {region.label}
                </option>
              ))}
            </select>
          </div>

          <input
            className={style["button"]}
            type="submit"
            value={loading ? "Entrando..." : "Continuar"}
            disabled={loading}
          />
        </form>

        <ToastContainer />
      </main>
    </div>
  );
}

export default SelectRegion;
